import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Progress } from "../components/ui/progress";
import {
  Briefcase,
  FileText,
  CheckCircle,
  Clock,
  XCircle,
  ArrowRight,
  Plus,
  AlertTriangle,
  AlertCircle,
  TrendingUp,
  Building2,
  Calendar,
  Download,
  FileSpreadsheet,
  FileDown,
  Loader2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import CountdownTimer from "../components/CountdownTimer";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CURRENT_YEAR = new Date().getFullYear();

export default function DashboardCabinet() {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [enterprises, setEnterprises] = useState([]);
  const [bilans, setBilans] = useState([]);
  const [enterprisesWithoutBilan, setEnterprisesWithoutBilan] = useState([]);

  // Export handlers
  const handleExport = async (type, format) => {
    setExporting(true);
    try {
      const endpoint = type === "bilans" 
        ? `${API_URL}/export/cabinet/bilans?format=${format}`
        : `${API_URL}/export/cabinet/enterprises?format=${format}`;
      
      const response = await axios.get(endpoint, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = type === "bilans" 
        ? `mes_bilans_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`
        : `mes_entreprises_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Export ${format.toUpperCase()} téléchargé avec succès`);
    } catch (error) {
      console.error(/* PROD */ "Export failed:", error);
      toast.error("Erreur lors de l'export. Veuillez réessayer.");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [enterprisesRes, bilansRes] = await Promise.all([
          axios.get(`${API_URL}/enterprises`),
          axios.get(`${API_URL}/bilans`),
        ]);
        setEnterprises(enterprisesRes.data);
        setBilans(bilansRes.data);
        
        // Find enterprises without bilan for current year
        const currentYearBilans = bilansRes.data.filter(b => b.annee === CURRENT_YEAR - 1);
        const enterpriseIdsWithBilan = currentYearBilans.map(b => b.enterprise_id);
        const withoutBilan = enterprisesRes.data.filter(e => !enterpriseIdsWithBilan.includes(e.id));
        setEnterprisesWithoutBilan(withoutBilan);
      } catch (error) {
        console.error(/* PROD */ "Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getStatusCounts = () => {
    const currentYearBilans = bilans.filter(b => b.annee === CURRENT_YEAR - 1);
    return {
      total: bilans.length,
      brouillon: currentYearBilans.filter((b) => b.status === "brouillon").length,
      soumis: currentYearBilans.filter((b) => b.status === "soumis" || b.status === "en_controle").length,
      valide: currentYearBilans.filter((b) => b.status === "valide").length,
      rejete: currentYearBilans.filter((b) => b.status === "rejete").length,
    };
  };

  const statusCounts = getStatusCounts();
  const recentBilans = bilans.slice(0, 5);
  
  // Calculate progress
  const totalEnterprises = enterprises.length;
  const enterprisesWithBilan = totalEnterprises - enterprisesWithoutBilan.length;
  const progressPercent = totalEnterprises > 0 ? Math.round((enterprisesWithBilan / totalEnterprises) * 100) : 0;

  // Determine urgency level based on date
  const getUrgencyLevel = () => {
    const now = new Date();
    const deadline = new Date(CURRENT_YEAR, 2, 31); // March 31
    const daysLeft = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
    
    if (daysLeft < 0) return { level: "expired", color: "red", message: "Date limite dépassée !" };
    if (daysLeft <= 7) return { level: "critical", color: "red", message: `Plus que ${daysLeft} jour(s) !` };
    if (daysLeft <= 30) return { level: "warning", color: "amber", message: `${daysLeft} jours restants` };
    return { level: "normal", color: "blue", message: `${daysLeft} jours restants` };
  };

  const urgency = getUrgencyLevel();

  const StatCard = ({ title, value, icon: Icon, color, description, link }) => (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-3xl font-heading font-bold mt-1">{value}</p>
            {description && (
              <p className="text-xs text-muted-foreground mt-1">{description}</p>
            )}
          </div>
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${color}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
        {link && (
          <Link to={link} className="text-xs text-primary hover:underline mt-3 inline-flex items-center gap-1">
            Voir détails <ArrowRight className="w-3 h-3" />
          </Link>
        )}
      </CardContent>
    </Card>
  );

  const getStatusBadge = (status) => {
    const badges = {
      brouillon: "bg-gray-100 text-gray-700",
      soumis: "bg-amber-100 text-amber-700",
      en_controle: "bg-orange-100 text-orange-700",
      valide: "bg-green-100 text-green-700",
      rejete: "bg-red-100 text-red-700",
    };
    const labels = {
      brouillon: "Brouillon",
      soumis: "Soumis",
      en_controle: "En contrôle",
      valide: "Validé",
      rejete: "Rejeté",
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[status] || "bg-gray-100"}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-cabinet">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Bienvenue, {user?.prenom}
          </h1>
          <p className="text-muted-foreground mt-1">
            Gérez vos entreprises et bilans sociaux
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={exporting} data-testid="export-cabinet-btn">
                {exporting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Exporter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport("bilans", "excel")} data-testid="export-bilans-excel">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Mes Bilans (Excel)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("bilans", "pdf")} data-testid="export-bilans-pdf">
                <FileDown className="w-4 h-4 mr-2" />
                Mes Bilans (PDF)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("enterprises", "excel")} data-testid="export-enterprises-excel">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Mes Entreprises (Excel)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("enterprises", "pdf")} data-testid="export-enterprises-pdf">
                <FileDown className="w-4 h-4 mr-2" />
                Mes Entreprises (PDF)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Link to="/entreprises">
            <Button variant="outline">
              <Briefcase className="w-4 h-4 mr-2" />
              Entreprises
            </Button>
          </Link>
          <Link to="/bilans/nouveau">
            <Button className="bg-primary hover:bg-primary/90">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau Bilan
            </Button>
          </Link>
        </div>
      </div>

      {/* Progress Card - Enterprises without bilan alert */}
      {enterprises.length > 0 && (
        <Card className={`border-2 ${
          enterprisesWithoutBilan.length === 0 
            ? "border-green-200 bg-green-50" 
            : urgency.level === "critical" || urgency.level === "expired"
              ? "border-red-200 bg-red-50"
              : urgency.level === "warning"
                ? "border-amber-200 bg-amber-50"
                : "border-blue-200 bg-blue-50"
        }`}>
          <CardContent className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
              {/* Progress Section */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  {enterprisesWithoutBilan.length === 0 ? (
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  ) : urgency.level === "critical" || urgency.level === "expired" ? (
                    <AlertTriangle className="w-6 h-6 text-red-600 animate-pulse" />
                  ) : (
                    <AlertCircle className="w-6 h-6 text-amber-600" />
                  )}
                  <div>
                    <h3 className="font-semibold text-lg">
                      {enterprisesWithoutBilan.length === 0 
                        ? "Toutes les déclarations sont faites !" 
                        : `${enterprisesWithoutBilan.length} entreprise(s) sans bilan ${CURRENT_YEAR - 1}`
                      }
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {urgency.message} - Date limite : 31 mars {CURRENT_YEAR}
                    </p>
                  </div>
                </div>
                
                {/* Progress Bar */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Progression des déclarations</span>
                    <span className="font-semibold">{enterprisesWithBilan}/{totalEnterprises} entreprises</span>
                  </div>
                  <Progress 
                    value={progressPercent} 
                    className={`h-3 ${
                      progressPercent === 100 ? "[&>div]:bg-green-500" : 
                      progressPercent >= 50 ? "[&>div]:bg-blue-500" : "[&>div]:bg-amber-500"
                    }`}
                  />
                  <p className="text-right text-sm font-medium">
                    {progressPercent}% complété
                  </p>
                </div>
              </div>

              {/* Countdown */}
              <div className="lg:w-64">
                <CountdownTimer />
              </div>
            </div>

            {/* Enterprises without bilan list */}
            {enterprisesWithoutBilan.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="font-medium text-sm text-gray-700 mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Entreprises nécessitant une déclaration :
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {enterprisesWithoutBilan.slice(0, 6).map((enterprise) => (
                    <div 
                      key={enterprise.id} 
                      className="flex items-center justify-between p-3 bg-white rounded-lg border shadow-sm"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">{enterprise.raison_sociale}</p>
                        <p className="text-xs text-muted-foreground">{enterprise.region}</p>
                      </div>
                      <Link to={`/bilans/nouveau?enterprise=${enterprise.id}`}>
                        <Button size="sm" variant="outline" className="ml-2 shrink-0">
                          <Plus className="w-3 h-3 mr-1" />
                          Créer
                        </Button>
                      </Link>
                    </div>
                  ))}
                </div>
                {enterprisesWithoutBilan.length > 6 && (
                  <p className="text-sm text-muted-foreground mt-3 text-center">
                    + {enterprisesWithoutBilan.length - 6} autre(s) entreprise(s)
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Alert for rejected bilans */}
      {statusCounts.rejete > 0 && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <XCircle className="w-5 h-5 text-red-600" />
          <div className="flex-1">
            <p className="font-medium text-red-800">
              {statusCounts.rejete} bilan(s) rejeté(s) nécessitant des corrections
            </p>
            <p className="text-sm text-red-600">
              Veuillez corriger et soumettre à nouveau ces bilans.
            </p>
          </div>
          <Link to="/bilans?status=rejete">
            <Button variant="outline" size="sm" className="border-red-300 text-red-700 hover:bg-red-100">
              Corriger
            </Button>
          </Link>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          title="Entreprises"
          value={enterprises.length}
          icon={Briefcase}
          color="bg-slate-700"
          description="Total des clients"
          link="/entreprises"
        />
        <StatCard
          title="Brouillons"
          value={statusCounts.brouillon}
          icon={FileText}
          color="bg-slate-500"
          description="À compléter"
          link="/bilans?status=brouillon"
        />
        <StatCard
          title="En Attente"
          value={statusCounts.soumis}
          icon={Clock}
          color="bg-amber-500"
          description="Validation IGT"
          link="/bilans?status=soumis"
        />
        <StatCard
          title="Validés"
          value={statusCounts.valide}
          icon={CheckCircle}
          color="bg-emerald-600"
          description={`Année ${CURRENT_YEAR - 1}`}
          link="/bilans?status=valide"
        />
        <StatCard
          title="Rejetés"
          value={statusCounts.rejete}
          icon={XCircle}
          color="bg-red-500"
          description="À corriger"
          link="/bilans?status=rejete"
        />
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Bilans */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-heading">Bilans Récents</CardTitle>
              <CardDescription>Vos dernières déclarations</CardDescription>
            </div>
            <Link to="/bilans">
              <Button variant="ghost" size="sm">
                Voir tout
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentBilans.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Aucun bilan créé</p>
                <Link to="/bilans/nouveau">
                  <Button variant="link" className="mt-2">
                    Créer votre premier bilan
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {recentBilans.map((bilan) => (
                  <Link
                    key={bilan.id}
                    to={`/bilans/${bilan.id}`}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors border"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">
                        {bilan.enterprise_name || "Entreprise"}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Année {bilan.annee}
                      </p>
                    </div>
                    {getStatusBadge(bilan.status)}
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Enterprises List */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-heading">Mes Entreprises</CardTitle>
              <CardDescription>Vos clients enregistrés</CardDescription>
            </div>
            <Link to="/entreprises">
              <Button variant="ghost" size="sm">
                Voir tout
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {enterprises.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Aucune entreprise enregistrée</p>
                <Link to="/entreprises">
                  <Button variant="link" className="mt-2">
                    Ajouter une entreprise
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {enterprises.slice(0, 5).map((enterprise) => {
                  const hasBilan = bilans.some(
                    b => b.enterprise_id === enterprise.id && b.annee === CURRENT_YEAR - 1
                  );
                  return (
                    <Link
                      key={enterprise.id}
                      to={`/entreprises/${enterprise.id}`}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors border"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{enterprise.raison_sociale}</p>
                        <p className="text-sm text-muted-foreground">
                          {enterprise.region} • {enterprise.secteur_activite}
                        </p>
                      </div>
                      {hasBilan ? (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Déclaré
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          À déclarer
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
